import { FEEDBACK_MOODS } from '@manasil/shared';
import { useState } from 'react';
import { Shell } from '../components/Shell.js';
import { Empty, Loading } from '../components/ui.js';
import { useFeedback, useJourneys } from '../lib/queries.js';

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const moodOf = (key: string) => FEEDBACK_MOODS.find((mood) => mood.key === key);

/**
 * The feedback inbox — what users said after finishing a journey (artboard 9a).
 *
 * One mood and one star rating per completed journey, newest first. CLAUDE.md's Week 3
 * asks for filters and a CSV export; the filters are here, the export can follow once
 * there is enough to export.
 */
export function FeedbackPage() {
  const [journeyId, setJourneyId] = useState('');
  const [rating, setRating] = useState<number | undefined>(undefined);
  const { data: journeys } = useJourneys();
  const { data, isLoading } = useFeedback({
    ...(journeyId ? { journeyId } : {}),
    ...(rating ? { rating } : {}),
  });
  const rows = data?.items ?? [];

  return (
    <Shell
      title="Feedback"
      subtitle="How finished journeys made people feel, and how they rate the app."
    >
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <select
          className="select"
          style={{ maxWidth: 320 }}
          value={journeyId}
          onChange={(event) => setJourneyId(event.target.value)}
        >
          <option value="">All journeys</option>
          {journeys?.items.map((journey) => (
            <option key={journey.id} value={journey.id}>
              {journey.title}
            </option>
          ))}
        </select>
        <div className="row" style={{ gap: 6 }}>
          <button
            type="button"
            className="chip"
            aria-pressed={rating === undefined}
            onClick={() => setRating(undefined)}
          >
            Any rating
          </button>
          {[5, 4, 3, 2, 1].map((stars) => (
            <button
              key={stars}
              type="button"
              className="chip"
              aria-pressed={rating === stars}
              onClick={() => setRating(stars)}
            >
              {stars} ★
            </button>
          ))}
        </div>
        {data ? (
          <span className="hint nums" style={{ marginLeft: 'auto' }}>
            {data.total} total · average {data.averageRating ?? '—'} ★
          </span>
        ) : null}
      </div>

      <div className="card">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty
            mark="✎"
            title="No feedback yet"
            hint={
              journeyId || rating
                ? 'Nothing matches this filter.'
                : 'Feedback arrives when someone finishes a journey and answers the two questions after the trophy.'
            }
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Journey</th>
                <th>Felt</th>
                <th>App rating</th>
                <th>Comment</th>
                <th>Who</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => {
                const mood = moodOf(entry.mood);
                return (
                  <tr key={entry.id}>
                    <td>{entry.journeyTitle}</td>
                    <td>
                      <span className="pill">
                        {mood ? `${mood.emoji} ${mood.label}` : entry.mood}
                      </span>
                    </td>
                    <td className="nums">
                      {'★'.repeat(entry.rating)}
                      <span className="dim">{'★'.repeat(5 - entry.rating)}</span>
                    </td>
                    <td className="hint" style={{ maxWidth: 320 }}>
                      {entry.comment ? `“${entry.comment}”` : '—'}
                    </td>
                    <td>
                      {entry.userName ?? <span className="dim">No name</span>}
                      <div className="hint nums">{entry.userMobile}</div>
                    </td>
                    <td className="hint nums">{when(entry.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Shell>
  );
}
